import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getUserService } from '@src/container';
import type { UserDTO } from '@src/application/users/dto/user.dto';

/**
 * Hook: useGetUser
 * Fetches a specific user by ID (Admin only)
 * 
 * @param userId - User ID to fetch
 * @param options - React Query options
 * @returns Query result with user data
 * 
 * @example
 * ```typescript
 * const { data: user, isLoading, error } = useGetUser('user-id-123');
 * ```
 */
export function useGetUser(
    userId: string | undefined,
    options?: Omit<UseQueryOptions<UserDTO | null, Error>, 'queryKey' | 'queryFn'>
) {
    const userService = getUserService();

    return useQuery({
        queryKey: ['users', userId],
        queryFn: () => userService.getById(userId!),
        enabled: !!userId,
        staleTime: 5 * 1000, // 5 seconds
        ...options,
    });
}
